import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useContext } from 'react';
import { UserContext } from '../App';

const empty = {
  vin: '', year: '', make: '', model: '', trim: '', mileage: '', price: '',
  internal_cost: '', down_payment: '', weekly_payment: '', monthly_payment: '',
  color_exterior: '', color_interior: '', engine: '', transmission: '',
  drivetrain: '', fuel_type: '', body_style: '', description: '',
  price_display: 'full_price', facebook_price: '', exclude_facebook: false,
};

export default function AddVehicle() {
  const { id } = useParams();
  const { isAdmin } = useContext(UserContext);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [vinLoading, setVinLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [vinStatus, setVinStatus] = useState('');

  useEffect(() => {
    if (id) {
      api.getVehicle(id).then(v => {
        setForm({
          vin: v.vin || '', year: v.year || '', make: v.make || '', model: v.model || '',
          trim: v.trim || '', mileage: v.mileage || '', price: v.price || '',
          internal_cost: v.internal_cost || '', down_payment: v.down_payment || '',
          weekly_payment: v.weekly_payment || '', monthly_payment: v.monthly_payment || '',
          color_exterior: v.color_exterior || '', color_interior: v.color_interior || '',
          engine: v.engine || '', transmission: v.transmission || '',
          drivetrain: v.drivetrain || '', fuel_type: v.fuel_type || '',
          body_style: v.body_style || '', description: v.description || '',
          price_display: v.price_display || 'full_price',
          facebook_price: v.facebook_price || '', exclude_facebook: v.exclude_facebook || false,
        });
        setPhotos(v.photos || []);
      });
    }
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const decodeVin = async () => {
    if (form.vin.length !== 17) { alert('Please enter a valid 17-digit VIN'); return; }
    setVinLoading(true); setVinStatus('Decoding...');
    const d = await api.decodeVin(form.vin.toUpperCase());
    setVinLoading(false);
    if (d.error) { setVinStatus('Decode failed'); return; }
    setForm(f => ({ ...f, year: d.year || f.year, make: d.make || f.make, model: d.model || f.model,
      trim: d.trim || f.trim, engine: d.engine || f.engine, transmission: d.transmission || f.transmission,
      drivetrain: d.drivetrain || f.drivetrain, fuel_type: d.fuel_type || f.fuel_type, body_style: d.body_style || f.body_style }));
    setVinStatus(`✓ Decoded: ${d.year} ${d.make} ${d.model}`);
  };

  const generateDescription = async () => {
    if (!form.year || !form.make || !form.model) { alert('Please fill in year, make, and model first'); return; }
    setAiLoading(true);
    const d = await api.generateDescription({ ...form, color: form.color_exterior, down: form.down_payment, weekly: form.weekly_payment });
    setAiLoading(false);
    if (d.description) set('description', d.description);
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    await api.deletePhoto(photoId);
    setPhotos(photos.filter(p => p.id !== photoId));
  };

  const setPrimary = async (photoId) => {
    await fetch(`/api/photos/${photoId}/primary`, {
      method: 'PUT',
      headers: { 'Authorization': 'Bearer ' + localStorage.getItem('rz_token') }
    });
    setPhotos(photos.map(p => ({ ...p, is_primary: p.id === photoId })));
  };

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files);
    setPendingPhotos(files);
  };

  const save = async () => {
    setSaving(true);
    const data = { ...form, vin: form.vin.toUpperCase() };
    let vehicle;
    if (id) {
      vehicle = await api.updateVehicle(id, { ...data, status: 'available' });
    } else {
      vehicle = await api.saveVehicle(data);
    }
    if (vehicle.error) { alert(vehicle.error); setSaving(false); return; }
    if (pendingPhotos.length > 0) {
      await api.uploadPhotos(vehicle.id, pendingPhotos);
    }
    setSaving(false);
    navigate('/inventory');
  };

  return (
    <div>
      <div className="page-header">
        <h1>{id ? 'Edit' : 'Add'} <span>Vehicle</span></h1>
      </div>

      <div className="form-section">
        <h3>VIN Decoder</h3>
        <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
          <input style={{ flex: 1, padding: '10px 14px', background: '#111', border: '1px solid #333', borderRadius: 8, color: '#e0e0e0', fontSize: 14, outline: 'none', textTransform: 'uppercase' }}
            type="text" placeholder="Enter 17-digit VIN..." maxLength={17} value={form.vin}
            onChange={e => set('vin', e.target.value.toUpperCase())} />
          <button className="btn-red" onClick={decodeVin} disabled={vinLoading}>{vinLoading ? 'Decoding...' : 'Decode VIN'}</button>
        </div>
        {vinStatus && <div style={{ fontSize: 12, color: vinStatus.includes('✓') ? '#4caf50' : '#f44336' }}>{vinStatus}</div>}
      </div>

      <div className="form-section">
        <h3>Vehicle Details</h3>
        <div className="form-grid-3">
          {[['year','Year','2020'],['make','Make','Toyota'],['model','Model','Camry'],['trim','Trim','LE'],['mileage','Mileage','45000'],['body_style','Body Style','Sedan'],['color_exterior','Exterior Color','Silver'],['color_interior','Interior Color','Gray'],['engine','Engine','2.5L'],['transmission','Transmission','Automatic'],['drivetrain','Drivetrain','FWD'],['fuel_type','Fuel Type','Gasoline']].map(([k, label, ph]) => (
            <div key={k} className="form-group"><label>{label}</label>
              <input type={k === 'year' || k === 'mileage' ? 'number' : 'text'} placeholder={ph} value={form[k]} onChange={e => set(k, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <h3>Pricing</h3>
        <div className="form-grid">
          <div className="form-group"><label>Asking Price <span style={{ color: '#555', fontSize: 10 }}>(optional)</span></label><input type="number" placeholder="12995" value={form.price} onChange={e => set('price', e.target.value)} /></div>
          {isAdmin && <div className="form-group"><label>Internal Cost <span style={{ color: '#555', fontSize: 10 }}>(private)</span></label><input type="number" placeholder="8500" value={form.internal_cost} onChange={e => set('internal_cost', e.target.value)} /></div>}
          <div className="form-group"><label>Down Payment</label><input type="number" placeholder="1500" value={form.down_payment} onChange={e => set('down_payment', e.target.value)} /></div>
          <div className="form-group"><label>Weekly Payment</label><input type="number" placeholder="125" value={form.weekly_payment} onChange={e => set('weekly_payment', e.target.value)} /></div>
          <div className="form-group"><label>Monthly Payment</label><input type="number" placeholder="499" value={form.monthly_payment} onChange={e => set('monthly_payment', e.target.value)} /></div>
          {isAdmin && <div className="form-group"><label>Price Display Mode</label>
            <select value={form.price_display} onChange={e => set('price_display', e.target.value)}>
              <option value="full_price">Show Full Price</option>
              <option value="payment_only">Payment Only (Down + Weekly)</option>
              <option value="call_for_price">Call for Price</option>
            </select>
          </div>}
          {isAdmin && <div className="form-group"><label>Facebook Price</label><input type="number" placeholder="Optional — for Facebook Marketplace" value={form.facebook_price} onChange={e => set('facebook_price', e.target.value)} /></div>}
          {isAdmin && <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 28 }}>
            <input type="checkbox" id="exclude-fb" style={{ width: 'auto', margin: 0 }} checked={form.exclude_facebook} onChange={e => set('exclude_facebook', e.target.checked)} />
            <label htmlFor="exclude-fb" style={{ textTransform: 'none', fontSize: 14, color: '#e0e0e0' }}>Exclude from Facebook Marketplace</label>
          </div>}
        </div>
      </div>

      <div className="form-section">
        <h3>Description</h3>
        <button className="ai-btn" onClick={generateDescription} disabled={aiLoading}>
          {aiLoading ? <><span className="spinner" /> Generating...</> : <><span>🤖</span> Generate AI Description</>}
        </button>
        <div className="form-group"><textarea placeholder="Vehicle description..." value={form.description} onChange={e => set('description', e.target.value)} style={{ minHeight: 120 }} /></div>
      </div>

      <div className="form-section">
        <h3>Photos</h3>
        {photos.length > 0 && (
          <div className="photo-grid" style={{ marginBottom: 16 }}>
            {photos.map(p => (
              <div key={p.id} className={`photo-thumb ${p.is_primary ? 'primary' : ''}`} style={{ position: 'relative' }}>
                <img src={p.url} alt="vehicle" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', gap: 2, padding: 4, background: 'rgba(0,0,0,.7)' }}>
                  {!p.is_primary && (
                    <button onClick={() => setPrimary(p.id)} style={{ flex: 1, padding: '2px 4px', background: '#1a4a1a', border: '1px solid #4caf50', borderRadius: 3, color: '#4caf50', cursor: 'pointer', fontSize: 9 }}>★ Primary</button>
                  )}
                  {p.is_primary && (
                    <span style={{ flex: 1, padding: '2px 4px', background: '#1a4a1a', border: '1px solid #4caf50', borderRadius: 3, color: '#4caf50', fontSize: 9, textAlign: 'center' }}>★ Primary</span>
                  )}
                  <button onClick={() => deletePhoto(p.id)} style={{ padding: '2px 6px', background: '#1a0000', border: '1px solid #ff4444', borderRadius: 3, color: '#ff4444', cursor: 'pointer', fontSize: 9 }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="photo-upload-area" onClick={() => document.getElementById('photo-input').click()}>
          <div style={{ fontSize: 32 }}>📷</div>
          <p style={{ color: '#666', marginTop: 8 }}>{pendingPhotos.length > 0 ? `${pendingPhotos.length} photos selected` : 'Click to upload photos'}</p>
          <p style={{ fontSize: 12, color: '#444', marginTop: 4 }}>JPG, PNG up to 20MB each</p>
        </div>
        <input type="file" id="photo-input" multiple accept="image/*" style={{ display: 'none' }} onChange={handlePhotos} />
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <button className="btn-red" onClick={save} disabled={saving}>{saving ? 'Saving...' : id ? 'Update Vehicle' : 'Save Vehicle'}</button>
        {id && <button className="btn-outline" onClick={() => navigate(`/vehicles/tag/${id}`)}>🏷️ Print Lot Tag</button>}
        <button className="btn-outline" onClick={() => navigate('/inventory')}>Cancel</button>
      </div>
    </div>
  );
}
